/**
 * Section Detail Component
 *
 * Collapsible panel for one ATS report section (contact, summary, experience,
 * skills, education). Shows:
 * - Score badge + label
 * - Feedback text
 * - Tips list
 * - Findings grouped by severity
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { cx, getSubScoreVariant, getSeverityColor } from '../utils/score-helpers';
import type { SectionDetailProps } from '../types';
import type { Finding } from '../../../types/ats-report';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const FindingItem: React.FC<{ finding: Finding }> = ({ finding }) => {
  const severityIcons: Record<string, string> = {
    critical: '!!',
    warning: '!',
    info: 'i',
  };

  return (
    <div className={cx(
      'ats-report-finding',
      `ats-report-finding--${finding.severity}`,
    )}>
      <span
        className="ats-report-finding__badge"
        style={{ backgroundColor: getSeverityColor(finding.severity) }}
      >
        {severityIcons[finding.severity]}
      </span>
      <span className="ats-report-finding__message">{finding.message}</span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const SectionDetail: React.FC<SectionDetailProps> = ({
  section,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const variant = getSubScoreVariant(section.score);

  const criticalFindings = section.findings.filter((f) => f.severity === 'critical');
  const warningFindings = section.findings.filter((f) => f.severity === 'warning');
  const infoFindings = section.findings.filter((f) => f.severity === 'info');

  const hasFindings = section.findings.length > 0;
  const hasTips = section.tips.length > 0;

  return (
    <div className={cx(
      'ats-report-section',
      `ats-report-section--${variant}`,
    )}>
      {/* Header (clickable) */}
      <button
        type="button"
        className="ats-report-section__header"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className={cx(
          'ats-report-section__score-badge',
          `ats-report-section__score-badge--${variant}`,
        )}>
          {section.score}/10
        </span>
        <span className="ats-report-section__label">{section.label}</span>
        <span className="ats-report-section__chevron">
          {expanded ? (
            <ChevronUp className="ats-report-section__chevron-icon" />
          ) : (
            <ChevronDown className="ats-report-section__chevron-icon" />
          )}
        </span>
      </button>

      {/* Body (collapsible) */}
      {expanded && (
        <div className="ats-report-section__body">
          {/* Feedback */}
          <p className="ats-report-section__feedback">{section.feedback}</p>

          {/* Tips */}
          {hasTips && (
            <div className="ats-report-section__tips">
              <div className="ats-report-section__tips-header">
                <Sparkles className="ats-report-section__tips-icon" />
                <span className="ats-report-section__tips-title">Tips</span>
              </div>
              <ul className="ats-report-section__tips-list">
                {section.tips.map((tip, i) => (
                  <li key={i} className="ats-report-section__tip-item">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Findings by severity */}
          {hasFindings && (
            <div className="ats-report-section__findings">
              {criticalFindings.map((f, i) => (
                <FindingItem key={`critical-${i}`} finding={f} />
              ))}
              {warningFindings.map((f, i) => (
                <FindingItem key={`warning-${i}`} finding={f} />
              ))}
              {infoFindings.map((f, i) => (
                <FindingItem key={`info-${i}`} finding={f} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

SectionDetail.displayName = 'SectionDetail';

export default SectionDetail;
